'use client'

import { Container, Flex } from '@quark/layout'
import Link from 'next/link'
import { Text } from '@quark/typography'
import { CenterFocusedCarousel } from '@quark/carousel'
import Image from 'next/image'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { CarouselBannerProps } from '@interfaces/home'
import { useHybridIsMobile } from '@utils/fallbackIsMobile'
import styles from './carouselBanner.module.scss'

const CarouselBanner = ({ sectionHeading, bannerList, isMobile }: CarouselBannerProps) => {
  const hybridIsMobile = useHybridIsMobile(isMobile ?? false)
  const handleBannerBtnClick = async (
    eventProperties: {
      eventName?: string
      customProperties?: Record<string, any>
    },
    url: string,
    urlInNewTab: boolean
  ): Promise<void> => {
    await trackEvent(eventProperties?.eventName ?? 'btn_explore_insurance', {
      type: 'banner_carousel',
      page_type: 'acko_home_page',
      redirection_url: url,
      ...eventProperties?.customProperties,
    })
    window.open(url, urlInNewTab ? '_blank' : '_self')
  }
  const bannerContent = bannerList?.map((banner, idx) => ({
    content: (
      <Link
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={() =>
          handleBannerBtnClick(banner?.eventProps ?? {}, banner?.redirectionUrl ?? '/', banner?.urlInNewTab ?? false)
        }
        href={banner?.redirectionUrl ?? '/'}
        target={banner?.urlInNewTab ? '_blank' : '_self'}
        rel="noopener noreferrer">
        <div key={idx} className={styles.banner}>
          <PropertyControlledComponent
            controllerProperty={!!banner?.bgVideoDesktop?.url || !!banner?.bgThumbnailDesktop?.url}
            fallback={null}>
            <video
              className={styles.bgVideoDesktop}
              poster={banner?.bgThumbnailDesktop?.url ?? undefined}
              loop
              autoPlay
              muted
              playsInline
              disableRemotePlayback
              disablePictureInPicture>
              <source src={banner?.bgVideoDesktop?.url ?? undefined} />
            </video>
          </PropertyControlledComponent>
          <PropertyControlledComponent
            controllerProperty={!!banner?.bgVideoMobile?.url || !!banner?.bgThumbnailMobile?.url}
            fallback={null}>
            <video
              className={styles.bgVideoMobile}
              poster={banner?.bgThumbnailMobile?.url ?? undefined}
              loop
              autoPlay
              muted
              playsInline
              disableRemotePlayback
              disablePictureInPicture>
              <source src={banner?.bgVideoMobile?.url ?? undefined} />
            </video>
          </PropertyControlledComponent>
          <div className={styles.bannerContent}>
            <Flex
              flexDirection="column"
              gap={{ lg: 'spacing-12', md: 'spacing-12', sm: 'spacing-8' }}
              alignItems={{ lg: 'flex-start', sm: 'center' }}>
              <PropertyControlledComponent
                controllerProperty={!!banner?.mobileLogo?.url}
                fallback={<div className={styles.extraSpacing} />}>
                <Image
                  className={styles.mobileLogo}
                  src={banner?.mobileLogo?.url ?? ''}
                  alt={banner?.mobileLogo?.name ?? ''}
                  width={130}
                  height={34}
                />
              </PropertyControlledComponent>
              <PropertyControlledComponent controllerProperty={!!banner?.heading} fallback={null}>
                <Text
                  as="span"
                  display={{ lg: 'block', md: 'block', sm: 'none' }}
                  fontStyle={{ lg: 'um_text-3xl/bold', md: 'um_text-2xl/bold', sm: 'um_text-lg/bold' }}
                  color="static/Primary-onDark"
                  whiteSpace="pre">
                  {banner?.heading}
                </Text>
              </PropertyControlledComponent>
              <PropertyControlledComponent controllerProperty={!!banner?.subHeading} fallback={null}>
                <Text
                  as="span"
                  display={{ lg: 'block', md: 'block', sm: 'none' }}
                  fontStyle={{
                    lg: 'um_text-base/normal',
                    md: 'um_text-sm/normal',
                    sm: 'um_text-xs/normal',
                  }}
                  color="static/Secondary-onDark"
                  whiteSpace="pre">
                  {banner?.subHeading}
                </Text>
              </PropertyControlledComponent>
              <PropertyControlledComponent controllerProperty={!!banner?.mobileHeading} fallback={null}>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'none', sm: 'block' }}
                  fontStyle={{ lg: 'um_text-3xl/bold', md: 'um_text-2xl/bold', sm: 'um_text-lg/bold' }}
                  color="static/Primary-onDark"
                  whiteSpace="pre">
                  {banner?.mobileHeading}
                </Text>
              </PropertyControlledComponent>
              <PropertyControlledComponent controllerProperty={!!banner?.mobileSubHeading} fallback={null}>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'none', sm: 'block' }}
                  fontStyle={{
                    lg: 'um_text-base/normal',
                    md: 'um_text-sm/normal',
                    sm: 'um_text-xs/normal',
                  }}
                  color="static/Secondary-onDark"
                  whiteSpace="pre">
                  {banner?.mobileSubHeading}
                </Text>
              </PropertyControlledComponent>
            </Flex>
            <PropertyControlledComponent controllerProperty={!!banner?.ctaText} fallback={null}>
              <div className={styles.bannerButton}>{banner?.ctaText}</div>
            </PropertyControlledComponent>
          </div>
        </div>
      </Link>
    ),
  }))
  return (
    <Container
      id={NewHomePageComponent.BannerCarousel}
      type="max-parent"
      backgroundColor="base/overlay"
      paddingTop={{ lg: 'spacing-80', md: 'spacing-56', sm: 'spacing-40' }}
      paddingBottom={{ lg: 'spacing-48', md: 'spacing-32', sm: 'spacing-24' }}>
      <Flex flexDirection="column" gap={{ lg: 'spacing-64', md: 'spacing-32', sm: 'spacing-32' }} alignItems="center">
        <Text
          fontStyle={{ lg: 'um_text-4xl/bold', md: 'um_text-3xl/bold', sm: 'um_text-2xl/bold' }}
          color="base/primary"
          textAlign="center">
          {sectionHeading ?? ''}
        </Text>
        <CenterFocusedCarousel
          borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
          content={bannerContent}
          pauseOnHover
          autoScroll
          isMobile={hybridIsMobile}
          loop
          showArrows={!hybridIsMobile}
          autoScrollInterval={4000}
        />
      </Flex>
    </Container>
  )
}

CarouselBanner.displayName = 'Banner'

export default CarouselBanner
